import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Eyebrow } from "@/components/Eyebrow";
import { StrikeSwap } from "@/components/StrikeSwap";

const CARDS = [
    {
        tag: null,
        title: "Property Management & Leasing",
        body: "Lead capture systems, live KPI dashboards, follow-up automation.",
        from: "Copy each applicant by hand",
        to: "Lead captured and routed on its own",
    },
    {
        tag: null,
        title: "Service Businesses",
        body: "Booking, quoting, follow-up, the admin that eats your evenings.",
        from: "Quotes typed out after hours",
        to: "Quote sent before you close the laptop",
    },
    {
        tag: null,
        title: "Your industry",
        body: "If your team does the same manual work every week, we can probably replace it. The diagnostic tells us both, fast.",
        from: "The same manual work every week",
        to: "A system that does it for you",
    },
];

function IndustryCard({ card, index }) {
    const [hover, setHover] = useState(false);
    const [auto, setAuto] = useState(false);
    const fired = useRef(false);
    const timers = useRef([]);

    useEffect(() => () => timers.current.forEach(clearTimeout), []);

    // hover / focus on desktop, plus a single auto fire when scrolled into view
    // (touch), which then reverts so hover can replay it afterward.
    const active = hover || auto;

    return (
        <motion.div
            data-testid={`industry-card-${index}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: index * 0.08 }}
            onViewportEnter={() => {
                if (fired.current) return;
                fired.current = true;
                const delay = index * 250;
                timers.current.push(setTimeout(() => setAuto(true), delay));
                timers.current.push(setTimeout(() => setAuto(false), delay + 2200));
            }}
            onHoverStart={() => setHover(true)}
            onHoverEnd={() => setHover(false)}
            onFocus={() => setHover(true)}
            onBlur={() => setHover(false)}
            tabIndex={0}
            className="group rounded-3xl border border-border bg-background p-8 flex flex-col justify-between min-h-[340px] transition-colors hover:bg-surface/50 focus:outline-none focus:ring-2 focus:ring-foreground/20"
        >
            <div>
                {card.tag && (
                    <span className="inline-flex items-center gap-2 text-xs font-medium mb-5">
                        <span className="h-1.5 w-1.5 rounded-full bg-foreground" />
                        {card.tag}
                    </span>
                )}
                <h3 className="font-display font-semibold text-2xl tracking-tight leading-snug">{card.title}</h3>
                <p className="mt-4 text-muted-foreground leading-relaxed">{card.body}</p>
            </div>

            <div className="mt-8 pt-6 border-t border-border">
                <StrikeSwap
                    testid={`industry-swap-${index}`}
                    from={card.from}
                    to={card.to}
                    active={active}
                    className="text-[0.95rem] font-medium"
                />
            </div>
        </motion.div>
    );
}

export function Industries() {
    return (
        <section id="industries" data-testid="industries" className="py-20 sm:py-24">
            <div className="max-w-6xl mx-auto px-5 sm:px-8">
                <Eyebrow>Industries</Eyebrow>
                <h2 className="mt-5 max-w-3xl font-display font-semibold tracking-[-0.02em] text-4xl sm:text-5xl leading-[1.08]">
                    Built where the manual work lives<span className="period">.</span>
                </h2>

                <div className="mt-14 grid md:grid-cols-3 gap-5">
                    {CARDS.map((c, i) => (
                        <IndustryCard key={i} card={c} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
